import { useDispatch, useSelector } from 'react-redux'

import { createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'

import { soyshareApi } from '../../../services/api'
import Socket from '../../../services/socket'

// CONSTANTS
export const reportStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED'
}

// INITIAL STATE
const initialState = {
  showDrawer: false,
  isListening: false
}

// REDUCER
const slice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    toggleShowDrawer: (state) => {
      state.showDrawer = !state.showDrawer
    },
    setIsListening: (state, { payload: isListening }) => {
      state.isListening = isListening
    }
  }
})

export const { actions } = slice
export default slice

// SELECTORS
const state = (state) => state.reports

const showDrawer = createSelector(state, (reports) => reports.showDrawer)

// HOOKS
export const useReportsDrawerState = () => {
  const dispatch = useDispatch()

  return {
    showDrawer: useSelector(showDrawer),
    toggleShowDrawer: () => dispatch(actions.toggleShowDrawer())
  }
}

// API
const startReportsListener = async ({ getState, dispatch }) => {
  const isListening = getState().reports.isListening

  if (!isListening) {
    dispatch(actions.setIsListening(true))

    const connection = await Socket.getConnection('reports')

    connection.on('report-status', async (data) => {
      if (data?.status === reportStatus.CANCELED) return

      dispatch(
        api.util.updateQueryData('getBackgroundReports', undefined, (draft) => {
          const targetIndex = draft.findIndex((report) => report.id === data.id)
          const targetReport = draft[targetIndex]

          if ([reportStatus.COMPLETED, data.status].includes(targetReport.status)) return

          draft[targetIndex] = { ...targetReport, status: data.status }

          const hasPendingReports = draft.some((report) =>
            [reportStatus.PENDING, reportStatus.PROCESSING].includes(report.status)
          )

          if (!hasPendingReports) {
            Socket.stopConnection('reports')
            dispatch(actions.setIsListening(false))
          }
        })
      )
    })

    connection.on('disconnect', () => {
      dispatch(actions.setIsListening(false))
    })

    connection.on('connect', () => {
      dispatch(actions.setIsListening(true))
    })

    connection.emit('listen-report-status')
  }
}

export const api = soyshareApi.injectEndpoints({
  endpoints: (build) => ({
    getBackgroundReports: build.query({
      query: () => ({ url: 'reports' }),
      providesTags: ['background-reports'],
      onQueryStarted: async (_, { dispatch, getState, queryFulfilled }) => {
        const { data: reports } = await queryFulfilled

        const pendingReports = [reportStatus.PENDING, reportStatus.PROCESSING]
        const hasPendingReports = reports.some((report) => pendingReports.includes(report.status))
        if (hasPendingReports) {
          await startReportsListener({ getState, dispatch })
        }
      }
    }),

    requestReport: build.mutation({
      query: ({ reportType, payload }) => ({ url: `reports/${reportType}`, method: 'POST', body: payload }),
      onQueryStarted: async (_, { queryFulfilled, dispatch, getState }) => {
        try {
          await startReportsListener({ getState, dispatch })

          const { data } = await queryFulfilled

          dispatch(
            api.util.updateQueryData('getBackgroundReports', undefined, (draft) => {
              draft.unshift(data)
            })
          )

          dispatch(actions.toggleShowDrawer())
        } catch (err) {
          console.error(err)
        }
      }
    }),

    cancelReport: build.mutation({
      query: (id) => ({
        url: `reports/${id}`,
        method: 'PUT',
        body: { status: reportStatus.CANCELED }
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          api.util.updateQueryData('getBackgroundReports', undefined, (draft) => {
            return draft.filter(({ id: reportId }) => reportId !== id)
          })
        )

        try {
          await queryFulfilled
        } catch (err) {
          console.error(err)
          patchResult.undo()
        }
      }
    })
  })
})

export const { useRequestReportMutation, useGetBackgroundReportsQuery, useCancelReportMutation } = api
